import React, { useState, useEffect, useRef } from 'react';
import { Image as LucideImage } from 'lucide-react';
import { Skeleton } from './Skeleton';
import { cn } from '../../lib/utils';
import { optimizeImageUrl } from '../../utils/imageOptimizer';

// Global memory cache of successfully loaded image URLs to ensure 0ms instantaneous display on revisit
const loadedImageUrls = new Set<string>();

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackSrc?: string;
  className?: string;
  wrapperClassName?: string;
  targetWidth?: number;
  targetQuality?: number;
  priority?: boolean;
}

export const ImageWithFallback = ({
  src,
  alt,
  fallbackSrc = '',
  className,
  wrapperClassName,
  targetWidth = 600,
  targetQuality = 75,
  priority = false,
  ...props
}: ImageWithFallbackProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Apply ultra-fast CDN optimization
  const optimizedInitial = optimizeImageUrl(src || fallbackSrc || undefined, targetWidth, targetQuality);
  const [imgSrc, setImgSrc] = useState<string | undefined>(optimizedInitial);

  // Check if image was previously loaded in memory
  const isPrecached = Boolean(optimizedInitial && loadedImageUrls.has(optimizedInitial));
  
  // High-priority / eager images are immediately in view
  const isEager = priority || props.loading === 'eager' || isPrecached;
  const [isInView, setIsInView] = useState<boolean>(isEager);
  const [isLoaded, setIsLoaded] = useState<boolean>(isPrecached);
  const [error, setError] = useState<boolean>(false);

  // Intersection Observer for intelligent lazy loading
  useEffect(() => {
    if (isInView) return;

    // Fallback if browser doesn't support IntersectionObserver
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first && first.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        // Pre-fetch 350px before entering viewport so images appear with 0 perceived wait time
        rootMargin: '350px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isInView]);

  // If the upstream `src` prop changes, re-optimize and reset state
  useEffect(() => {
    const nextUrl = optimizeImageUrl(src || fallbackSrc || undefined, targetWidth, targetQuality);
    setImgSrc(nextUrl);
    setError(false);

    if (nextUrl && loadedImageUrls.has(nextUrl)) {
      setIsLoaded(true);
      setIsInView(true);
    } else {
      setIsLoaded(false);
      if (isEager) {
        setIsInView(true);
      }
    }
  }, [src, fallbackSrc, targetWidth, targetQuality, isEager]);

  return (
    <div 
      ref={containerRef}
      className={cn("relative overflow-hidden flex items-center justify-center bg-slate-50", wrapperClassName)}
    >
      {/* 1. Instant Shimmer Skeleton while not in view or downloading */}
      {(!isLoaded || !isInView) && !error && (
        <div className="absolute inset-0 z-0">
          <Skeleton className="w-full h-full bg-slate-100 animate-pulse" />
        </div>
      )}

      {/* 2. Error Fallback state */}
      {error || !imgSrc ? (
        <div className="flex flex-col items-center justify-center w-full h-full bg-slate-50 text-slate-300 p-4">
          <LucideImage size={props.width ? Number(props.width) / 4 : 28} className="opacity-40 text-slate-400" />
          <span className="text-[9px] mt-1.5 font-bold uppercase tracking-wider text-slate-400">Hamim Catalog</span>
        </div>
      ) : (
        /* 3. Lazy loaded image with Intersection Observer trigger */
        isInView && (
          <img
            src={imgSrc}
            alt={alt || 'Surgical Instrument'}
            decoding="async"
            loading={isEager ? "eager" : "lazy"}
            onLoad={() => {
              if (imgSrc) loadedImageUrls.add(imgSrc);
              setIsLoaded(true);
            }}
            onError={() => {
              const nextFallback = optimizeImageUrl(fallbackSrc, targetWidth, targetQuality);
              if (imgSrc !== nextFallback && nextFallback) {
                setImgSrc(nextFallback);
              } else {
                setError(true);
              }
            }}
            className={cn(
              "w-full h-full object-contain transition-opacity duration-300",
              isLoaded ? "opacity-100" : "opacity-0",
              className
            )}
            {...props}
          />
        )
      )}
    </div>
  );
};
